import type {
  ApparelProductProps,
  Brand,
  SeasonID,
  Variant,
} from '@velasca/types'
import { getProductCardImage } from '@velasca/utils/get-product-card-image'
import { isSeason } from '@velasca/utils/is-season'

interface ProductCollections {
  title: string
  handle: string
}

interface ProductVariant {
  title: string
  handle: string
  price: {
    min: number
    max: number
  }
  compare_at_price: string
  featured_image: string
  images: string
  material: string
  color: string
  sole: string
  sole_color: string
  variant_colors: string
  variant_color_style: string
  reviews_count: string
  reviews_average: string
  materials_filter: string[]
  colors_filter: string[]
}

// questi tipi corrispondono a quelli in packages/velasca-api-algolia/src/types.ts
export interface FormattedVariantProps {
  objectID: string
  title: string
  handle: string
  product_type: number
  custom_type: string
  status: string
  price: {
    min: number
    max: number
  }
  compare_at_price: {
    min: number
    max: number
  }
  collections: ProductCollections[]
  featured_image: string
  images: string[]
  available_sizes: string[]
  available_skus: string[]
  material: string
  color: string
  sole: string
  sole_color: string
  heel: string
  lining: string
  lining_color: string
  construction: string
  style: string
  fit: string
  madein: string
  season: string
  usage: string
  design: string
  stuffing: string
  texture: string
  pockets: string
  buttons: string
  collar: string
  closure: string
  features: string
  portability: string
  accessories: string
  size_and_fit: string
  variant_colors: string
  variant_color_style: string
  material_short: string
  color_short: string
  sole_color_hex: string
  texture_image_name: string
  pattern_image_name: string
  staff_pick_icon_id: string
  is_new: boolean
  collection_brand_id: Brand
  collection_brand_name: string
  season_id: SeasonID
  season_name: string
  reviews_count: string
  reviews_average: string
  materials_filter: string[]
  colors_filter: string[]
  variants: ProductVariant[]
  jolly: string
  [key: string]: unknown
}

// TODO: controllare che tutte le logiche di formatApparelData o groupByProduct siano riportate anche qui
const getFormattedVariant = ({
  handle,
  price,
  compare_at_price,
  images,
  color_short,
  material_short,
  texture_image_name,
  variant_color_style,
  pattern_image_name,
  sole_color_hex,
  variant_colors,
  reviews_count,
  reviews_average,
  is_new,
  staff_pick_icon_id,
  season_id,
  season_name,
  collection_brand_id,
  collection_brand_name,
  product_type,
  objectID,
  color,
  material,
  jolly,
}: FormattedVariantProps): Variant => {
  const validSeasonID = season_id && isSeason(season_id) ? season_id : undefined

  return {
    handle,
    id: objectID,
    price,
    compareAtPrice: compare_at_price,
    description: {
      bold: color_short || color,
      thin: material_short || material,
    },
    img: getProductCardImage({
      images,
      customProductType: product_type,
    }),
    reviews: {
      count: reviews_count,
      average: reviews_average,
    },
    variantDot: {
      colors: variant_colors ? JSON.parse(variant_colors) : [],
      sole: sole_color_hex ?? null,
      pattern: variant_color_style ?? null,
      texture: texture_image_name ?? null,
      img: pattern_image_name ?? null,
    },
    staffPickIconID: staff_pick_icon_id ?? null,
    isNew: is_new ?? false,
    collectionBrandName: collection_brand_name,
    collectionBrandID: collection_brand_id,
    seasonID: validSeasonID,
    seasonName: season_name,
    jolly,
  }
}

export const formatAlgoliaHit = (
  hit: FormattedVariantProps,
): ApparelProductProps => {
  return {
    title: hit.title,
    productType: hit.custom_type,
    collectionBrandID: hit.collection_brand_id,
    variants: [getFormattedVariant(hit)],
  }
}
